import React, { useState } from 'react';
import { motion } from 'framer-motion';

const Support = () => {
  const [activeTab, setActiveTab] = useState('faq');

  const faqs = [
    {
      question: 'Do your products come with warranty?',
      answer: 'Only specific products. For items without warranty, we provide triple quality checks (factory, shipping, warehouse) before delivery.'
    },
    {
      question: 'Are the products original and brand new?',
      answer: 'Yes, everything we sell is 100% brand new and authentic. We do not sell used or refurbished products.'
    },
    {
      question: 'How long is delivery in Singapore?',
      answer: 'Standard Delivery: 2–3 working days\n\nExpress Delivery: Same/Next day (via Lalamove, Grab, Foodpanda, etc.)\n\nSelf-collect / Meetup: Available on request'
    },
    {
      question: 'What payment methods do you accept?',
      answer: 'We accept:\n\n• PayNow / PayLah!\n• Cash on Delivery (for Meetup & Self-collect)'
    },
    {
      question: 'Can I return or exchange an item?',
      answer: 'We currently do not accept returns or exchanges. However, every product goes through triple quality checks (factory, shipping, warehouse) to ensure you receive items in perfect condition.'
    },
    {
      question: 'How do I place an order?',
      answer: '1. Add items to cart\n\n2. Choose Meetup / Self-collect / Delivery option\n\n3. Checkout ➝ Confirm via WhatsApp or Google Form\n\n4. Delivery or collection will be arranged with you directly'
    }
  ];

  const contactMethods = [
    {
      icon: '📧',
      title: 'Email Support',
      value: 'support@chillgaming.sg',
      description: 'Reply within 24 hours'
    },
    {
      icon: '💬',
      title: 'WhatsApp Support',
      value: '+60 11-1424 0380',
      description: '(customer service)'
    },
    {
      icon: '📍',
      title: 'Meetup Location',
      value: 'Available at Sengkang & Orchard locations',
      description: 'Self-collect available'
    }
  ];

  return (
    <div className="min-h-screen bg-dark-400 pt-24 pb-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-4xl md:text-5xl font-futuristic font-bold mb-4 text-white">
            Customer Support
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            We are committed to providing you with the best pre-sales and after-sales service
          </p>
        </motion.div>

        {/* Tab Navigation */}
        <motion.div
          className="flex justify-center mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <div className="bg-dark-300 rounded-lg p-1 flex space-x-1">
            {[
              { id: 'faq', name: 'FAQ', icon: '❓' },
              { id: 'contact', name: 'Contact Us', icon: '📞' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 flex items-center space-x-2 ${
                  activeTab === tab.id
                    ? 'bg-primary-500 text-white'
                    : 'text-gray-400 hover:text-white hover:bg-white/10'
                }`}
              >
                <span>{tab.icon}</span>
                <span>{tab.name}</span>
              </button>
            ))}
          </div>
        </motion.div>

        {/* FAQ Tab */}
        {activeTab === 'faq' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl font-bold text-white mb-8 text-center">Frequently Asked Questions</h2>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <motion.div
                    key={index}
                    className="card-glass rounded-lg overflow-hidden"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                  >
                    <details className="group">
                      <summary className="p-6 cursor-pointer list-none">
                        <div className="flex items-center justify-between">
                          <h3 className="text-lg font-semibold text-white group-open:text-primary-400 transition-colors">
                            {faq.question}
                          </h3>
                          <svg
                            className="w-5 h-5 text-gray-400 group-open:rotate-180 transition-transform duration-200"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </summary>
                      <div className="px-6 pb-6">
                        <div className="text-gray-300 leading-relaxed whitespace-pre-line">
                          {faq.answer}
                        </div>
                      </div>
                    </details>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {/* Contact Tab */}
        {activeTab === 'contact' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl font-bold text-white mb-8 text-center">Contact Methods</h2>
              <div className="space-y-6">
                {contactMethods.map((method, index) => (
                  <motion.div
                    key={index}
                    className="card-glass p-6 rounded-lg"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    whileHover={{ scale: 1.02 }}
                  >
                    <div className="flex items-center space-x-4">
                      <div className="text-3xl">{method.icon}</div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-white mb-1">
                          {method.title}
                        </h3>
                        <p className="text-primary-400 font-medium mb-1">
                          {method.value}
                        </p>
                        <p className="text-gray-400 text-sm">
                          {method.description}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        )}

      </div>
    </div>
  );
};

export default Support;